import { Button, Drawer, Form, Input, InputNumber, Space, Typography, Select, Empty, Spin, Tabs, App } from 'antd';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { pageFormItemLayout } from '@utils/property';
import { useParams } from 'react-router-dom';
import { saveSponsor, updateSponsor, getSponsor, hallList, getConferenceBenefitList, conferenceOnceApi } from '@services/Conference';
import { getBossStaffs, getAllEnterpriseApi } from '@services/Enterprise';
import { getOrganizations } from '@services/Experts';
import config from '@/config';
import debounce from 'lodash/debounce';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import BenefitCategorySection from './components/BenefitCategorySection';
import ForumBenefitSection from './components/ForumBenefitSection';
import SelectBenefitModal from './components/SelectBenefitModal';
import HallScheduleSortableItem from './components/HallScheduleSortableItem';
import HallPlanImageModal from '../HallPlanImageModal';
import { TITLE_OPTIONS, BENEFIT_CATEGORIES } from '../../constants';
import { transformBenefitListToState, assembleBenefitListPayload } from './utils/benefitTransform';
import OperationLogDrawerTrigger from '@/components/common/OperationLogDrawerTrigger';
import { OperationLogDrawerHostProvider } from '@/components/common/operationLogDrawerHostContext';
import { logOrgBenefitCreate, logOrgBenefitUpdate } from '@/utils/conferenceLogger';
import { buildOrgBenefitLogPayloadFromEditorSnapshot } from '@/utils/conferenceOpLogSnapshot';
import { buildForumHallTreeData } from './utils/forumHallTree';
import { isBoothSelectType, hasAvailableBoothNumbers, parseBoothNumbers } from './utils/boothNumbers';
import './index.less';

const { Text } = Typography;

/** 保存时必填项未通过校验的统一顶部提示 */
const SUBMIT_REQUIRED_TIP = '请完善权益必填信息';

/** 保存/编辑接口失败且无 message 时的兜底提示 */
const API_ERROR_FALLBACK = '接口异常，请稍后重试';

/** 抽屉顶部锚点 Tabs：key 与表单分区 ref 一一对应 */
const ANCHOR_TABS = [
    { key: 'basic', label: '基本信息' },
    { key: 'benefit', label: '合同权益' },
    { key: 'schedule', label: '日程展示' },
    { key: 'notes', label: '备注' },
];

/** 滚动联动阈值：分区顶部距滚动容器顶部小于该值（px）即视为已到达 */
const ANCHOR_ACTIVE_OFFSET = 12;

/** 点击锚点 Tab 后滚动联动的锁定时长（ms，覆盖 smooth 滚动过程） */
const ANCHOR_SCROLL_LOCK_MS = 800;

/**
 * 解析会场规划图：接口返回数组字符串（'["url1","url2"]'）或数组，相对路径补全 OSS 前缀
 * @param {string|Array} raw 会议 hallPlanImages 原始值
 * @returns {string[]}
 */
function parseHallPlanImages(raw) {
    if (!raw) return [];
    let arr = raw;
    if (typeof arr === 'string') {
        try {
            arr = JSON.parse(arr);
        } catch {
            arr = arr ? [arr] : [];
        }
    }
    if (!Array.isArray(arr)) return [];
    return arr.filter(Boolean).map(url => (url.includes('http') ? url : config.OSS_PREFIX + url));
}

/**
 * 获取接口失败时的 toast 文案
 * @param {{ message?: string }|null|undefined} res 接口响应
 * @returns {string}
 */
function getApiErrorMessage(res) {
    return res?.message || API_ERROR_FALLBACK;
}

/**
 * 将接口 customerManager 回显为表单 phone 数组
 * 兼容逗号分隔字符串或对象数组 [{ id, name, phone, status }]
 * @param {string|Array<{ phone?: string }>|null|undefined} customerManager
 * @returns {string[]}
 */
function parseCustomerManagerPhones(customerManager) {
    if (!customerManager) return [];
    if (Array.isArray(customerManager)) {
        return customerManager.map(item => String(item?.phone ?? '').trim()).filter(Boolean);
    }
    return String(customerManager)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
}

/**
 * 将表单选中的 phone 数组转为接口所需逗号拼接字符串
 * @param {string[]} phones
 * @returns {string}
 */
function formatCustomerManagerForApi(phones) {
    if (!phones?.length) return '';
    return phones.filter(phone => phone != null && phone !== '').join(',');
}

/**
 * 判断购买数量是否已填写（与 validateBenefits 规则一致）
 * @param {Object} benefit 权益 state
 * @returns {boolean}
 */
function isBenefitQuantityFilled(benefit) {
    return benefit.quantity != null && benefit.quantity !== '';
}

/**
 * 判断展位是否已选择（与 validateBenefits 规则一致）
 * @param {Object} benefit 权益 state
 * @returns {boolean}
 */
function isBenefitBoothFilled(benefit) {
    return parseBoothNumbers(benefit.boothNumbers).length > 0;
}

/**
 * 权益行字段变更后，清除已通过校验的错误项
 * @param {Object} prevErrors 当前错误 Map
 * @param {string|number} conferenceBenefitId 权益 ID
 * @param {Object} benefit 更新后的权益 state
 * @returns {Object}
 */
function clearBenefitRowErrors(prevErrors, conferenceBenefitId, benefit) {
    const current = prevErrors[conferenceBenefitId];
    if (!current) return prevErrors;

    const nextFieldErrors = { ...current };
    if (isBoothSelectType(benefit)) {
        if (isBenefitBoothFilled(benefit)) {
            delete nextFieldErrors.boothNumbers;
        }
    } else if (isBenefitQuantityFilled(benefit)) {
        delete nextFieldErrors.quantity;
    }

    if (Object.keys(nextFieldErrors).length === 0) {
        const next = { ...prevErrors };
        delete next[conferenceBenefitId];
        return next;
    }
    return { ...prevErrors, [conferenceBenefitId]: nextFieldErrors };
}

/**
 * 论坛权益行字段变更后，清除已通过校验的错误项
 * @param {Object} prevErrors 论坛错误 Map
 * @param {number} forumIndex 论坛块索引
 * @param {string|number} conferenceBenefitId 权益 ID
 * @param {Object} benefit 更新后的权益 state
 * @returns {Object}
 */
function clearForumBenefitRowErrors(prevErrors, forumIndex, conferenceBenefitId, benefit) {
    const blockErrors = prevErrors[forumIndex];
    if (!blockErrors?.[conferenceBenefitId]) return prevErrors;

    const current = blockErrors[conferenceBenefitId];
    const nextFieldErrors = { ...current };
    if (isBoothSelectType(benefit)) {
        if (isBenefitBoothFilled(benefit)) {
            delete nextFieldErrors.boothNumbers;
        }
    } else if (isBenefitQuantityFilled(benefit)) {
        delete nextFieldErrors.quantity;
    }

    const nextBlock = { ...blockErrors };
    if (Object.keys(nextFieldErrors).length === 0) {
        delete nextBlock[conferenceBenefitId];
    } else {
        nextBlock[conferenceBenefitId] = nextFieldErrors;
    }

    const next = { ...prevErrors };
    if (Object.keys(nextBlock).length === 0) {
        delete next[forumIndex];
    } else {
        next[forumIndex] = nextBlock;
    }
    return next;
}

/**
 * 新增/编辑机构权益抽屉
 */
const AddOrganizationDrawer = ({ visibleAddOrgsDrawer, setVisibleAddOrgsDrawer, sponsorList, organization, setOrganization }) => {
    const params = useParams();
    const conferenceId = params?.id || 0;
    const [form] = Form.useForm();
    const { message, modal } = App.useApp();

    const [submitLoading, setSubmitLoading] = useState(false);
    const [conferenceHallList, setConferenceHallList] = useState([]);
    const [orgOptions, setOrgOptions] = useState([]);
    const [orgSearchLoading, setOrgSearchLoading] = useState(false);
    const fetchRef = useRef(0);
    const [searchValue, setSearchValue] = useState(null);
    const [currentAddOrg, setCurrentAddOrg] = useState(null);

    const [allBenefits, setAllBenefits] = useState([]);
    const [benefitLoading, setBenefitLoading] = useState(false);
    const benefitFetchRef = useRef(0);
    const [staffs, setStaffs] = useState([]);
    const [enterprisePerson, setEnterprisePerson] = useState([]);
    /** 机构客户列表已加载标记，避免每次开抽屉重复请求（数据量大：pageSize 9999） */
    const enterprisePersonLoadedRef = useRef(false);
    const [selectedBenefits, setSelectedBenefits] = useState({});
    const [forumBenefits, setForumBenefits] = useState([]);
    const [benefitErrors, setBenefitErrors] = useState({});
    const [forumBenefitErrors, setForumBenefitErrors] = useState({});
    const [forumHallTreeData, setForumHallTreeData] = useState([]);
    const [forumHallNameMap, setForumHallNameMap] = useState({});

    const [selectModalOpen, setSelectModalOpen] = useState(false);
    const [selectModalCategory, setSelectModalCategory] = useState({ id: 0, name: '' });
    const [selectModalForumIndex, setSelectModalForumIndex] = useState(null);
    const [selectModalCurrentSelected, setSelectModalCurrentSelected] = useState([]);

    /** 会场规划图（会议级 hallPlanImages，大会展览展示分类的查看入口用） */
    const [hallPlanImages, setHallPlanImages] = useState([]);
    const [hallPlanModalOpen, setHallPlanModalOpen] = useState(false);

    const initialSnapshotRef = useRef(null);

    /** 当前高亮的锚点 Tab */
    const [activeAnchor, setActiveAnchor] = useState(ANCHOR_TABS[0].key);
    const sectionRefs = useRef({});
    /** 表单外层包裹 div，其父节点即抽屉滚动容器（.ant-drawer-body） */
    const formWrapRef = useRef(null);
    /** 滚动联动锁：程序化平滑滚动期间避免中间态覆盖点击的 Tab */
    const scrollSpyLockRef = useRef(false);
    const scrollSpyLockTimerRef = useRef(null);

    /** 分区 ref 回调（固定引用，避免每次渲染重建导致 ref 抖动） */
    const sectionRefCallbacks = useMemo(() => {
        const map = {};
        for (const { key } of ANCHOR_TABS) {
            map[key] = el => {
                sectionRefs.current[key] = el;
            };
        }
        return map;
    }, []);

    /** 获取抽屉滚动容器 */
    const getScrollContainer = useCallback(() => formWrapRef.current?.parentElement || null, []);

    /**
     * 滚动联动：容器滚动时同步高亮当前所在分区的锚点 Tab
     */
    const handleBodyScroll = useCallback(() => {
        if (scrollSpyLockRef.current) return;
        const container = getScrollContainer();
        if (!container) return;
        const containerTop = container.getBoundingClientRect().top;
        let current = ANCHOR_TABS[0].key;
        for (const { key } of ANCHOR_TABS) {
            const el = sectionRefs.current[key];
            if (el && el.getBoundingClientRect().top - containerTop <= ANCHOR_ACTIVE_OFFSET) {
                current = key;
            }
        }
        // 滚动触底时末尾分区可能到不了容器顶部，强制高亮最后一项（内容不足一屏无滚动时跳过）
        if (container.scrollHeight > container.clientHeight + 2 && container.scrollTop + container.clientHeight >= container.scrollHeight - 2) {
            current = ANCHOR_TABS[ANCHOR_TABS.length - 1].key;
        }
        setActiveAnchor(prev => (prev === current ? prev : current));
    }, [getScrollContainer]);

    /**
     * 点击锚点 Tab：平滑滚动到对应分区，滚动期间锁定滚动联动
     */
    const handleAnchorChange = useCallback(
        key => {
            setActiveAnchor(key);
            const container = getScrollContainer();
            const target = sectionRefs.current[key];
            if (!container || !target) return;
            const top = container.scrollTop + (target.getBoundingClientRect().top - container.getBoundingClientRect().top);
            scrollSpyLockRef.current = true;
            window.clearTimeout(scrollSpyLockTimerRef.current);
            scrollSpyLockTimerRef.current = window.setTimeout(() => {
                scrollSpyLockRef.current = false;
            }, ANCHOR_SCROLL_LOCK_MS);
            container.scrollTo({ top, behavior: 'smooth' });
        },
        [getScrollContainer],
    );

    const closeDrawer = useCallback(() => {
        setVisibleAddOrgsDrawer(false);
        form.resetFields();
        setSelectedBenefits({});
        setForumBenefits([]);
        setBenefitErrors({});
        setForumBenefitErrors({});
        setCurrentAddOrg(null);
        setOrganization(null);
        setOrgSearchLoading(false);
        setSearchValue(null);
        setOrgOptions([]);
    }, [form, setVisibleAddOrgsDrawer, setOrganization]);

    /**
     * 取消按钮脏检查
     */
    const handleCancel = useCallback(() => {
        const currentSnapshot = JSON.stringify({
            form: form.getFieldsValue(),
            benefits: selectedBenefits,
            forums: forumBenefits,
            org: currentAddOrg,
        });

        if (initialSnapshotRef.current && currentSnapshot !== initialSnapshotRef.current) {
            modal.confirm({
                title: '提示',
                content: '发现有数据变动，确认不保存并关闭？',
                okText: '确认',
                cancelText: '取消',
                centered: true,
                onOk: () => closeDrawer(),
            });
        } else {
            closeDrawer();
        }
    }, [form, selectedBenefits, forumBenefits, currentAddOrg, modal, closeDrawer]);

    /**
     * 拉取会议权益池；弹窗每次打开与抽屉初始化时调用，保证总量/已售等数据最新
     */
    const loadAllBenefits = useCallback(async () => {
        benefitFetchRef.current += 1;
        const fetchId = benefitFetchRef.current;
        setBenefitLoading(true);
        try {
            const res = await getConferenceBenefitList({ conferenceId });
            if (fetchId !== benefitFetchRef.current) return;
            if (res.status_code === 200 && res.data) {
                setAllBenefits(res.data);
            } else {
                setAllBenefits([]);
            }
        } catch (_) {
            if (fetchId !== benefitFetchRef.current) return;
            setAllBenefits([]);
        } finally {
            if (fetchId !== benefitFetchRef.current) return;
            setBenefitLoading(false);
        }
    }, [conferenceId]);

    const loadStaffs = useCallback(async () => {
        try {
            const res = await getBossStaffs();
            if (res.status_code === 200) {
                setStaffs(res.data || []);
            }
        } catch (_) {}
    }, []);

    /**
     * 加载机构客户列表。
     * 使用 ref 标记是否已加载，避免每次开抽屉重复发起大数据量请求（pageSize: 9999）。
     */
    const loadEnterprisePerson = useCallback(async () => {
        if (enterprisePersonLoadedRef.current) return;
        try {
            const res = await getAllEnterpriseApi({ conferenceId: Number(conferenceId), page: 1, pageSize: 9999, role: 3 });
            if (res.status_code === 200 && res.data) {
                setEnterprisePerson(res.data.list || []);
                enterprisePersonLoadedRef.current = true;
            }
        } catch (_) {}
    }, [conferenceId]);

    const getHallsList = useCallback(async () => {
        const res = await hallList({ conferenceId });
        if (res.status_code === 200) {
            const options = res.data.map(item => ({
                label: item.name,
                value: item.id,
                children: item.secondHallsList?.map(i => ({ label: i.name, value: i.id, key: i.id })) || [],
                key: item.id,
            }));
            setConferenceHallList(options);

            const { treeData, hallNameMap } = buildForumHallTreeData(res.data);
            setForumHallTreeData(treeData);
            setForumHallNameMap(hallNameMap);
        }
    }, [conferenceId]);

    /**
     * 按关键字检索机构（dataengine organizations/search）
     * @param {string} keyword 检索关键字
     * @returns {Promise<Array<{ label: string, value: number, bodyType: number }>>}
     */
    const fetchOrgList = useCallback(async keyword => {
        if (!keyword) return [];
        const res = await getOrganizations({ keyword });
        if (res.status_code === 200 && res.data?.length) {
            return res.data.map(org => ({ label: org.name, value: org.id, bodyType: org.orgType }));
        }
        return [];
    }, []);

    const debounceFetcher = useMemo(() => {
        const loadOptions = value => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            const keyword = value?.trim();
            if (!keyword) {
                setOrgSearchLoading(false);
                setSearchValue(null);
                setOrgOptions([]);
                return;
            }
            setSearchValue(keyword);
            setOrgSearchLoading(true);
            setOrgOptions([]);
            fetchOrgList(keyword)
                .then(newOptions => {
                    if (fetchId !== fetchRef.current) return;
                    setOrgOptions(newOptions);
                })
                .catch(() => {
                    if (fetchId !== fetchRef.current) return;
                    setOrgOptions([]);
                })
                .finally(() => {
                    if (fetchId !== fetchRef.current) return;
                    setOrgSearchLoading(false);
                });
        };
        return debounce(loadOptions, 500);
    }, [fetchOrgList]);

    /** 选择权益弹窗每次打开时重新拉取权益池 */
    useEffect(() => {
        if (selectModalOpen) {
            loadAllBenefits();
        }
    }, [selectModalOpen, loadAllBenefits]);

    useEffect(() => {
        if (visibleAddOrgsDrawer) {
            getHallsList();
            loadAllBenefits();
            loadStaffs();
            loadEnterprisePerson();

            if (organization?.id) {
                getSponsor(organization.id).then(res => {
                    if (res.status_code === 200 && res.data) {
                        const data = res.data;
                        setOrgOptions([{ label: data.name, value: data.orgId }]);
                        setCurrentAddOrg({ label: data.name, value: data.orgId, bodyType: organization.orgType });

                        form.setFieldsValue({
                            name: data.orgId,
                            title: data.title || undefined,
                            contractAmount: data.contractAmount || undefined,
                            notes: data.notes || '',
                        });

                        if (data.customerManager) {
                            form.setFieldValue('customerManager', parseCustomerManagerPhones(data.customerManager));
                        }
                        if (data.expertId) {
                            form.setFieldValue(
                                'expertId',
                                data.expertId
                                    .split(',')
                                    .map(i => Number.parseInt(i))
                                    .filter(Boolean),
                            );
                        }
                        if (data.hallList?.length > 0) {
                            form.setFieldValue(
                                'hallList',
                                data.hallList.map(item => {
                                    const hallId = [item.hallId];
                                    if (item.pid) hallId.splice(0, 0, item.pid);
                                    return { hallId, sponsorType: item.sponsorType };
                                }),
                            );
                        }

                        getConferenceBenefitList({ conferenceId }).then(benefitRes => {
                            if (benefitRes.status_code === 200 && benefitRes.data) {
                                const allB = benefitRes.data;
                                setAllBenefits(allB);
                                const { selectedBenefits: sb, forumBenefits: fb } = transformBenefitListToState(data.benefitList, allB);
                                setSelectedBenefits(sb);

                                if (fb.length > 0 && res.data.hallList) {
                                    // no-op: hallName is optional display
                                }
                                setForumBenefits(fb);

                                setTimeout(() => {
                                    initialSnapshotRef.current = JSON.stringify({
                                        form: form.getFieldsValue(),
                                        benefits: sb,
                                        forums: fb,
                                        org: { label: data.name, value: data.orgId, bodyType: organization.orgType },
                                    });
                                }, 100);
                            }
                        });
                    }
                });
            } else {
                setTimeout(() => {
                    initialSnapshotRef.current = JSON.stringify({
                        form: form.getFieldsValue(),
                        benefits: {},
                        forums: [],
                        org: null,
                    });
                }, 100);
            }
        } else {
            setSelectedBenefits({});
            setForumBenefits([]);
            setBenefitErrors({});
            setForumBenefitErrors({});
            setOrganization(null);
            setCurrentAddOrg(null);
            form.resetFields();
            initialSnapshotRef.current = null;
        }
    }, [visibleAddOrgsDrawer]);

    /** 抽屉打开后：复位滚动位置与锚点高亮，并监听容器滚动驱动滚动联动 */
    useEffect(() => {
        if (!visibleAddOrgsDrawer) return undefined;
        setActiveAnchor(ANCHOR_TABS[0].key);
        const container = getScrollContainer();
        if (!container) return undefined;
        container.scrollTop = 0;
        container.addEventListener('scroll', handleBodyScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleBodyScroll);
    }, [visibleAddOrgsDrawer, handleBodyScroll, getScrollContainer]);

    /** 卸载时清理滚动联动锁定定时器 */
    useEffect(() => () => window.clearTimeout(scrollSpyLockTimerRef.current), []);

    /** 抽屉打开后拉取会议信息（会场规划图供大会展览展示分类查看入口展示） */
    useEffect(() => {
        if (!visibleAddOrgsDrawer) return undefined;
        let cancelled = false;
        conferenceOnceApi({ id: Number(conferenceId) })
            .then(res => {
                if (cancelled || res.status_code !== 200) return;
                setHallPlanImages(parseHallPlanImages(res.data?.hallPlanImages));
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [visibleAddOrgsDrawer, conferenceId]);

    /**
     * 校验权益数据
     * @returns {boolean} 校验通过返回 true
     */
    const validateBenefits = () => {
        const errors = {};
        const fErrors = {};
        let hasError = false;

        for (const [id, b] of Object.entries(selectedBenefits)) {
            const isBoothType = isBoothSelectType(b);
            if (isBoothType) {
                if (!hasAvailableBoothNumbers(b) && parseBoothNumbers(b.boothNumbers).length === 0) {
                    errors[id] = { boothNumbers: '未配置展位号，请在"权益管理"中配置' };
                    hasError = true;
                } else if (!b.boothNumbers) {
                    errors[id] = { boothNumbers: '请选择展位编号' };
                    hasError = true;
                }
            } else {
                if (b.quantity == null || b.quantity === '') {
                    errors[id] = { quantity: '请填写购买数量' };
                    hasError = true;
                }
            }
        }

        for (let fi = 0; fi < forumBenefits.length; fi++) {
            const forum = forumBenefits[fi];
            if (!forum.hallsId) {
                if (!fErrors[fi]) fErrors[fi] = {};
                fErrors[fi].hallsId = '请选择论坛';
                hasError = true;
            } else if (!forum.benefits?.length) {
                if (!fErrors[fi]) fErrors[fi] = {};
                fErrors[fi].benefits = '请选择论坛权益';
                hasError = true;
            }
            if (!forum.benefits?.length) continue;
            for (const b of forum.benefits) {
                const isBoothType = isBoothSelectType(b);
                if (isBoothType) {
                    if (!hasAvailableBoothNumbers(b) && parseBoothNumbers(b.boothNumbers).length === 0) {
                        if (!fErrors[fi]) fErrors[fi] = {};
                        fErrors[fi][b.conferenceBenefitId] = { boothNumbers: '未配置展位号，请在"权益管理"中配置' };
                        hasError = true;
                    } else if (!b.boothNumbers) {
                        if (!fErrors[fi]) fErrors[fi] = {};
                        fErrors[fi][b.conferenceBenefitId] = { boothNumbers: '请选择展位编号' };
                        hasError = true;
                    }
                } else {
                    if (b.quantity == null || b.quantity === '') {
                        if (!fErrors[fi]) fErrors[fi] = {};
                        fErrors[fi][b.conferenceBenefitId] = { quantity: '请填写购买数量' };
                        hasError = true;
                    }
                }
            }
        }

        setBenefitErrors(errors);
        setForumBenefitErrors(fErrors);

        if (hasError) {
            message.error(SUBMIT_REQUIRED_TIP);
        }
        return !hasError;
    };

    /**
     * 提交保存
     */
    const submitConferenceOrg = () => {
        form.validateFields()
            .then(values => {
                const hasBenefits = Object.keys(selectedBenefits).length > 0 || forumBenefits.some(f => f.benefits?.length > 0);
                const hasForumBlocks = forumBenefits.length > 0;
                if ((hasBenefits || hasForumBlocks) && !validateBenefits()) return;

                if (!currentAddOrg) {
                    message.error(SUBMIT_REQUIRED_TIP);
                    return;
                }

                const payload = { ...values };
                payload.conferenceId = conferenceId;
                payload.orgId = currentAddOrg.value;
                payload.name = currentAddOrg.label;
                if (currentAddOrg.bodyType != null) payload.orgType = currentAddOrg.bodyType;

                payload.customerManager = formatCustomerManagerForApi(payload.customerManager);
                if (payload.expertId?.length) {
                    payload.expertId = payload.expertId.join(',');
                } else {
                    payload.expertId = '';
                }

                if (payload.hallList?.length > 0) {
                    payload.hallList = payload.hallList.map(item => ({
                        hallId: Array.isArray(item.hallId) ? item.hallId[item.hallId.length - 1] : item.hallId,
                        sponsorType: item.sponsorType,
                        conferenceId,
                    }));
                } else {
                    payload.hallList = [];
                }

                payload.benefitList = assembleBenefitListPayload(selectedBenefits, forumBenefits, conferenceId, currentAddOrg.value, organization?.id ? organization.id : undefined);

                if (organization?.id) {
                    payload.id = organization.id;
                    doUpdate(payload);
                } else {
                    doSave(payload);
                }
            })
            .catch(() => {
                message.error(SUBMIT_REQUIRED_TIP);
            });
    };

    const doSave = async payload => {
        try {
            setSubmitLoading(true);
            const res = await saveSponsor(payload);
            if (res.status_code === 200) {
                try {
                    await logOrgBenefitCreate({
                        sponsorId: res.data?.id ?? res.data,
                        conferenceId,
                        afterPayload: payload,
                        isSuccess: true,
                    });
                } catch (logError) {
                    console.error('操作日志上报失败', logError);
                }
                message.success('保存成功');
                closeDrawer();
                sponsorList();
            } else {
                try {
                    await logOrgBenefitCreate({
                        sponsorId: null,
                        conferenceId,
                        afterPayload: payload,
                        isSuccess: false,
                        errorMessage: getApiErrorMessage(res),
                    });
                } catch (logError) {
                    console.error('操作日志上报失败', logError);
                }
                message.error(getApiErrorMessage(res));
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    const doUpdate = async payload => {
        const beforePayload = buildOrgBenefitLogPayloadFromEditorSnapshot(initialSnapshotRef.current, conferenceId, organization?.id);
        try {
            setSubmitLoading(true);
            const res = await updateSponsor(payload);
            if (res.status_code === 200) {
                try {
                    await logOrgBenefitUpdate({
                        sponsorId: organization?.id,
                        conferenceId,
                        beforePayload,
                        afterPayload: payload,
                        isSuccess: true,
                    });
                } catch (logError) {
                    console.error('操作日志上报失败', logError);
                }
                message.success('编辑成功');
                closeDrawer();
                sponsorList();
            } else {
                try {
                    await logOrgBenefitUpdate({
                        sponsorId: organization?.id,
                        conferenceId,
                        beforePayload,
                        afterPayload: payload,
                        isSuccess: false,
                        errorMessage: getApiErrorMessage(res),
                    });
                } catch (logError) {
                    console.error('操作日志上报失败', logError);
                }
                message.error(getApiErrorMessage(res));
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    // ── 权益选择弹窗 ──

    const openSelectModal = (categoryId, categoryName) => {
        const currentIds = Object.values(selectedBenefits)
            .filter(b => b.type === categoryId)
            .map(b => b.conferenceBenefitId);
        setSelectModalCategory({ id: categoryId, name: categoryName });
        setSelectModalCurrentSelected(currentIds);
        setSelectModalForumIndex(null);
        setSelectModalOpen(true);
    };

    const openForumSelectModal = forumIndex => {
        const forum = forumBenefits[forumIndex];
        const currentIds = (forum?.benefits || []).map(b => b.conferenceBenefitId);
        setSelectModalCategory({ id: 4, name: '论坛' });
        setSelectModalCurrentSelected(currentIds);
        setSelectModalForumIndex(forumIndex);
        setSelectModalOpen(true);
    };

    /**
     * 弹窗确认回调 - 将选中的权益合并到 state 中
     */
    const handleSelectConfirm = selectedItems => {
        setSelectModalOpen(false);

        if (selectModalForumIndex !== null) {
            setForumBenefits(prev => {
                const updated = [...prev];
                const forum = { ...updated[selectModalForumIndex] };
                const existingMap = {};
                for (const b of forum.benefits || []) {
                    existingMap[b.conferenceBenefitId] = b;
                }

                /**
                 * 构建单条新增权益 state
                 */
                const buildForumBenefit = item => {
                    let availableBoothNumbers = [];
                    if (item.boothNumbers) {
                        try {
                            availableBoothNumbers = JSON.parse(item.boothNumbers);
                        } catch (_) {}
                    }
                    return {
                        conferenceBenefitId: item.id,
                        benefitId: item.benefitId,
                        type: item.type,
                        benefitName: item.benefitName,
                        boothNumbers: '',
                        originalBoothNumbers: '',
                        availableBoothNumbers,
                        isBoothBenefit: item.isBoothBenefit,
                        builtin: item.builtin,
                        benefitDesc: item.benefitDesc || '',
                        priceReference: item.priceReference || '',
                        totalQuantity: item.quantity ?? null,
                        soldCount: item.soldCount ?? null,
                        soldBoothList: item.soldBoothList || [],
                        quantity: null,
                        remark: '',
                        hallsId: forum.hallsId || null,
                        id: null,
                        sponsorId: null,
                    };
                };

                // 已有项保持原有顺序排在前，新增项追加在末尾
                const newItems = selectedItems.filter(item => !existingMap[item.id]).map(buildForumBenefit);
                const existingItems = (forum.benefits || []).filter(b => selectedItems.find(item => item.id === b.conferenceBenefitId));
                forum.benefits = [...existingItems, ...newItems];

                updated[selectModalForumIndex] = forum;
                return updated;
            });
            if (selectedItems.length > 0) {
                const forumIndex = selectModalForumIndex;
                setForumBenefitErrors(prev => {
                    const blockErrors = prev[forumIndex];
                    if (!blockErrors?.benefits) return prev;
                    const next = { ...prev };
                    const block = { ...blockErrors };
                    delete block.benefits;
                    if (Object.keys(block).length === 0) delete next[forumIndex];
                    else next[forumIndex] = block;
                    return next;
                });
            }
        } else {
            setSelectedBenefits(prev => {
                const categoryId = selectModalCategory.id;
                const next = { ...prev };
                const prevIdsForCategory = Object.keys(next).filter(k => next[k].type === categoryId);
                for (const id of prevIdsForCategory) {
                    if (!selectedItems.find(item => item.id === Number(id))) {
                        delete next[id];
                    }
                }

                // 同批次新增项按勾选顺序追加在末尾：用基础时间 + 自增 i 保证组内有序
                const batchBaseTs = Date.now();
                let i = 0;
                for (const item of selectedItems) {
                    if (next[item.id]) continue;
                    let availableBoothNumbers = [];
                    if (item.boothNumbers) {
                        try {
                            availableBoothNumbers = JSON.parse(item.boothNumbers);
                        } catch (_) {}
                    }
                    next[item.id] = {
                        conferenceBenefitId: item.id,
                        benefitId: item.benefitId,
                        type: item.type,
                        benefitName: item.benefitName,
                        boothNumbers: '',
                        originalBoothNumbers: '',
                        availableBoothNumbers,
                        isBoothBenefit: item.isBoothBenefit,
                        builtin: item.builtin,
                        benefitDesc: item.benefitDesc || '',
                        priceReference: item.priceReference || '',
                        totalQuantity: item.quantity ?? null,
                        soldCount: item.soldCount ?? null,
                        soldBoothList: item.soldBoothList || [],
                        quantity: null,
                        remark: '',
                        hallsId: null,
                        id: null,
                        sponsorId: null,
                        /** 仅前端使用：用于稳定渲染顺序，提交时不会传给后端 */
                        __addedAt: batchBaseTs + i++,
                    };
                }
                return next;
            });
        }
    };

    // ── 权益行操作 ──

    const handleBenefitChange = (conferenceBenefitId, updated) => {
        setSelectedBenefits(prev => ({
            ...prev,
            // 透传 updated 时保留原 __addedAt，避免编辑某条权益后排序被打乱
            [conferenceBenefitId]: { ...updated, __addedAt: prev[conferenceBenefitId]?.__addedAt ?? updated.__addedAt ?? Date.now() },
        }));
        setBenefitErrors(prev => clearBenefitRowErrors(prev, conferenceBenefitId, updated));
    };

    const handleRemoveBenefit = conferenceBenefitId => {
        setSelectedBenefits(prev => {
            const next = { ...prev };
            delete next[conferenceBenefitId];
            return next;
        });
    };

    // ── 论坛操作 ──

    /**
     * 添加论坛块 - 追加到列表末尾，与用户阅读顺序一致
     */
    const handleAddForum = () => {
        setForumBenefits(prev => [...prev, { hallsId: null, hallName: '', benefits: [], _key: `forum_${Date.now()}` }]);
    };

    const handleRemoveForum = index => {
        setForumBenefits(prev => prev.filter((_, i) => i !== index));
        setForumBenefitErrors(prev => {
            const next = {};
            for (const [key, val] of Object.entries(prev)) {
                const i = Number(key);
                if (i < index) next[i] = val;
                else if (i > index) next[i - 1] = val;
            }
            return next;
        });
    };

    /**
     * 论坛 TreeSelect 变更；选中后清除该块的论坛必填错误
     */
    const handleForumChange = (index, hallsId, hallName) => {
        setForumBenefits(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], hallsId, hallName };
            return updated;
        });
        if (hallsId) {
            setForumBenefitErrors(prev => {
                const blockErrors = prev[index];
                if (!blockErrors?.hallsId) return prev;
                const next = { ...prev };
                const block = { ...blockErrors };
                delete block.hallsId;
                if (Object.keys(block).length === 0) delete next[index];
                else next[index] = block;
                return next;
            });
        }
    };

    const handleForumBenefitChange = (forumIndex, conferenceBenefitId, updated) => {
        setForumBenefits(prev => {
            const updated2 = [...prev];
            const forum = { ...updated2[forumIndex] };
            forum.benefits = forum.benefits.map(b => (b.conferenceBenefitId === conferenceBenefitId ? updated : b));
            updated2[forumIndex] = forum;
            return updated2;
        });
        setForumBenefitErrors(prev => clearForumBenefitRowErrors(prev, forumIndex, conferenceBenefitId, updated));
    };

    const handleForumRemoveBenefit = (forumIndex, conferenceBenefitId) => {
        setForumBenefits(prev => {
            const updated = [...prev];
            const forum = { ...updated[forumIndex] };
            forum.benefits = forum.benefits.filter(b => b.conferenceBenefitId !== conferenceBenefitId);
            updated[forumIndex] = forum;
            return updated;
        });
    };

    // ── 日程展示相关（保留已有逻辑）──

    const delConference = useCallback(
        index => {
            const arr = form.getFieldValue('hallList');
            let newArr = [];
            if (arr.length > 1) {
                newArr = [...arr];
                newArr.splice(index, 1);
            }
            form.setFieldValue('hallList', newArr);
        },
        [form],
    );

    const scheduleSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    return (
        <OperationLogDrawerHostProvider>
            <Drawer
                closable={{ placement: 'end' }}
                title={organization ? '编辑机构权益' : '新增机构权益'}
                size="100%"
                placement="bottom"
                push={false}
                open={visibleAddOrgsDrawer}
                onClose={handleCancel}
                layout="horizontal"
                mask={{ closable: false }}
                className="applog-record-drawer"
                classNames={{ header: 'add-org-drawer-header', extra: 'add-org-drawer-extra' }}
                extra={<Tabs className="add-org-anchor-tabs" activeKey={activeAnchor} items={ANCHOR_TABS} onChange={handleAnchorChange} />}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleCancel}>取消</Button>
                            <Button type="primary" loading={submitLoading} onClick={submitConferenceOrg}>
                                保存
                            </Button>
                        </Space>
                    </div>
                }
            >
                <div className="conference-form add-organization-form" ref={formWrapRef}>
                    {organization?.id ? <OperationLogDrawerTrigger entityId={organization.id} entityType="conference_sponsor" module="conference" /> : null}
                    <Form {...pageFormItemLayout} layout="horizontal" name="addOrgForm" form={form} autoComplete="off">
                        <div ref={sectionRefCallbacks.basic}>
                            {/* 机构名称 */}
                            <Form.Item name="name" label="机构名称" rules={[{ required: true, message: '请选择机构名称' }]}>
                                <Select
                                    allowClear
                                    optionLabelProp="label"
                                    showSearch={{ filterOption: false, onSearch: debounceFetcher }}
                                    placeholder="输入关键字检索机构"
                                    loading={orgSearchLoading}
                                    onChange={newValue => {
                                        const target = orgOptions.find(org => org.value === newValue);
                                        setCurrentAddOrg(target ? { label: target.label, value: target.value, bodyType: target.bodyType } : null);
                                    }}
                                    style={{ width: '100%' }}
                                    notFoundContent={
                                        orgSearchLoading ? (
                                            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                                                <Spin size="small" />
                                            </div>
                                        ) : searchValue ? (
                                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
                                        ) : (
                                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="请输入关键字检索" />
                                        )
                                    }
                                    options={orgOptions}
                                />
                            </Form.Item>

                            {/* 客户经理 */}
                            <Form.Item label="客户经理" name="customerManager">
                                <Select
                                    mode="multiple"
                                    allowClear
                                    optionLabelProp="label"
                                    showSearch={{
                                        filterOption: (input, option) => {
                                            const keyword = input.toLowerCase();
                                            const name = String(option?.label ?? '').toLowerCase();
                                            const phone = String(option?.value ?? '');
                                            return name.includes(keyword) || phone.includes(keyword);
                                        },
                                    }}
                                    placeholder="请选择客户经理"
                                >
                                    {staffs.map(staff => (
                                        <Select.Option key={staff.id} value={staff.phone} label={staff.name} disabled={staff.status === 1 || !staff.phone}>
                                            {staff.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {/* 机构客户 */}
                            <Form.Item label="机构客户" name="expertId" extra="机构客户选择，请先将机构客户添加至本项目“会议嘉宾”，并将参会人员类别设置为“企业人员”">
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="请选择机构客户"
                                    fieldNames={{ label: 'memUsername', value: 'id' }}
                                    options={enterprisePerson}
                                    showSearch={{
                                        optionFilterProp: 'children',
                                        filterOption: (input, option) => {
                                            const { memUsername } = option.props;
                                            return memUsername?.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                        },
                                    }}
                                />
                            </Form.Item>

                            {/* 名义 */}
                            <Form.Item name="title" label="名义">
                                <Select placeholder="请选择名义" allowClear options={TITLE_OPTIONS} />
                            </Form.Item>

                            {/* 金额 */}
                            <Form.Item name="contractAmount" label="金额（元）">
                                <InputNumber placeholder="输入合作金额" min={0} max={9999999999} style={{ width: '100%' }} />
                            </Form.Item>
                        </div>

                        {/* 合同权益 - 结构化 */}
                        <div ref={sectionRefCallbacks.benefit}>
                            <Form.Item label="合同权益">
                                <div className="benefit-section-content">
                                    {BENEFIT_CATEGORIES.map(cat =>
                                        cat.id === 4 ? (
                                            <ForumBenefitSection
                                                key={cat.id}
                                                forums={forumBenefits}
                                                hallTreeData={forumHallTreeData}
                                                hallNameMap={forumHallNameMap}
                                                onAddForum={handleAddForum}
                                                onRemoveForum={handleRemoveForum}
                                                onForumChange={handleForumChange}
                                                onSelectBenefit={openForumSelectModal}
                                                onBenefitChange={handleForumBenefitChange}
                                                onRemoveBenefit={handleForumRemoveBenefit}
                                                benefitErrors={forumBenefitErrors}
                                                isEditMode={!!organization?.id}
                                            />
                                        ) : (
                                            <BenefitCategorySection
                                                key={cat.id}
                                                categoryId={cat.id}
                                                categoryName={cat.name}
                                                benefits={Object.values(selectedBenefits)
                                                    .filter(b => b.type === cat.id)
                                                    .sort((a, b) => (a.__addedAt ?? 0) - (b.__addedAt ?? 0))}
                                                onSelectBenefit={openSelectModal}
                                                onBenefitChange={handleBenefitChange}
                                                onRemoveBenefit={handleRemoveBenefit}
                                                benefitErrors={benefitErrors}
                                                isEditMode={!!organization?.id}
                                                headerExtra={
                                                    cat.id === 2 ? (
                                                        <Button type="link" size="small" style={{ paddingInline: 0 }} onClick={() => setHallPlanModalOpen(true)}>
                                                            查看会场规划图
                                                        </Button>
                                                    ) : null
                                                }
                                            />
                                        ),
                                    )}
                                </div>
                            </Form.Item>
                        </div>

                        {/* 日程展示 */}
                        <div ref={sectionRefCallbacks.schedule}>
                            <Form.Item label="日程展示">
                                <Form.List name="hallList">
                                    {(fields, { add, move }) => {
                                        /**
                                         * 日程展示列表拖拽排序
                                         */
                                        const handleScheduleDragEnd = ({ active, over }) => {
                                            if (!over || active.id === over.id) return;
                                            const oldIndex = fields.findIndex(f => f.key === active.id);
                                            const newIndex = fields.findIndex(f => f.key === over.id);
                                            if (oldIndex >= 0 && newIndex >= 0) move(oldIndex, newIndex);
                                        };

                                        return (
                                            <>
                                                <div style={{ marginBottom: 8 }}>
                                                    <Button type="link" onClick={() => add()}>
                                                        <i className="bi bi-plus-circle" />
                                                        {'添加'}
                                                    </Button>
                                                    <Text type="secondary">将在用户端日程模块，对应会议下方展示本机构信息和单位类型</Text>
                                                </div>
                                                <DndContext sensors={scheduleSensors} collisionDetection={closestCenter} onDragEnd={handleScheduleDragEnd}>
                                                    <SortableContext items={fields.map(f => f.key)} strategy={verticalListSortingStrategy}>
                                                        {fields.map(field => (
                                                            <HallScheduleSortableItem key={field.key} id={field.key} field={field} conferenceHallList={conferenceHallList} onDelete={delConference} />
                                                        ))}
                                                    </SortableContext>
                                                </DndContext>
                                            </>
                                        );
                                    }}
                                </Form.List>
                            </Form.Item>
                        </div>

                        {/* 备注 */}
                        <div ref={sectionRefCallbacks.notes}>
                            <Form.Item label="备注" name="notes" extra="内容仅后台可见，方便内部管理">
                                <Input.TextArea rows={5} placeholder="" maxLength={5000} showCount />
                            </Form.Item>
                        </div>
                    </Form>
                </div>

                {/* 选择权益弹窗 */}
                <SelectBenefitModal open={selectModalOpen} categoryId={selectModalCategory.id} categoryName={selectModalCategory.name} allBenefits={allBenefits} loading={benefitLoading} currentSelected={selectModalCurrentSelected} onConfirm={handleSelectConfirm} onCancel={() => setSelectModalOpen(false)} />

                {/* 会场规划图弹窗（仅查看，不可上传/删除/保存） */}
                <HallPlanImageModal open={hallPlanModalOpen} mode="view" images={hallPlanImages} onCancel={() => setHallPlanModalOpen(false)} />
            </Drawer>
        </OperationLogDrawerHostProvider>
    );
};

export default AddOrganizationDrawer;
